import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  setActiveTab,
  clearSelectedModelTab,
  setFilterTags,
  setSelectedModelTab,
} from '../../../store/slices/projectSlice'
import {
  clearAllBoundingBoxes,
  selectAnnotatedPage,
  selectUnannotatedPage,
  setAnnotatedPage,
  setUnannotatedPage,
  setSelectedContentId,
  setEditingContent,
  clearEditingContent,
  clearAllBoundingBoxes as clearBBoxes,
  clearInitialBoundingBoxes,
  clearEditingTagIds,
  setSelectedBoundingBox,
  clearUndoStack,
  clearRedoStack,
  selectEditingContent,
} from '../../../store/slices/annotationSlice'
import AnnotationsSubView from '../subviews/AnnotationsSubView'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import AnnotationsReview from '../AnnotationsReview/AnnotationsReview'
import { Content } from '../../../types/models/content'
import { useFetchAnnotatedContentQuery } from '../../../services/contentApi'
import { useDatasetStats } from '../../../utils/datasetStats'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { selectFilterTags, selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectDatasetSources } from '../../../store/slices/datasetSlice'
import { Tag } from '../../../types/models/tag'

type ActiveTab = 'content' | 'annotated'

interface DatasetLabelingViewProps {
  datasetSources?: string[]
}

const DatasetLabelingView: React.FC<DatasetLabelingViewProps> = ({ datasetSources = ['All'] }) => {
  const dispatch = useAppDispatch()
  const [selectedContent, setSelectedContent] = useState<Content | null>(null)
  const [activeTab, setLocalActiveTab] = useState<ActiveTab>('content')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  // Redux selectors
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedFilterTags = useAppSelector(selectFilterTags) as Tag[]
  const annotatedPage = useAppSelector(selectAnnotatedPage)
  const unannotatedPage = useAppSelector(selectUnannotatedPage)
  const editingContent = useAppSelector(selectEditingContent)

  // Clear ALL state when project changes
  const prevProjectIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevProjectIdRef.current && prevProjectIdRef.current !== selectedProjectId) {
      // Project changed - clear ALL state including local component state
      setSelectedContent(null) // Clear local state
      setLocalActiveTab('content') // Reset to default tab
      dispatch(clearEditingContent())
      dispatch(setSelectedContentId(''))
      dispatch(clearBBoxes())
      dispatch(clearInitialBoundingBoxes())
    }
    prevProjectIdRef.current = selectedProjectId
  }, [selectedProjectId, dispatch])

  // Content query arguments
  const annotatedContentArgs = useMemo(
    () => ({
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      limit: 200,
      page: annotatedPage - 1,
      metadata_filter:
        datasetSources.length > 0 && !datasetSources.includes('All')
          ? { datasource: datasetSources }
          : undefined,
      sort_key: 'updated_at',
      sort_val: -1,
      tag_id:
        activeTab === 'annotated' &&
        selectedFilterTags?.length > 0 &&
        !selectedFilterTags.some((tag) => tag.name === 'Unlabeled')
          ? selectedFilterTags.map((t) => t.id)
          : undefined,
    }),
    [
      selectedProjectId,
      project?.datasetid,
      annotatedPage,
      datasetSources,
      activeTab,
      selectedFilterTags,
    ]
  )

  const unannotatedContentArgs = useMemo(
    () => ({
      project_id: selectedProjectId || '',
      dataset_id: undefined,
      limit: 200,
      page: unannotatedPage - 1,
      metadata_filter:
        datasetSources.length > 0 && !datasetSources.includes('All')
          ? { datasource: datasetSources }
          : undefined,
      sort_key: 'updated_at',
      sort_val: -1,
    }),
    [selectedProjectId, unannotatedPage, datasetSources]
  )

  const annotatedContentQuery = useMemo(
    () => ({
      skip: activeTab !== 'annotated',
    }),
    [activeTab]
  )

  const unannotatedContentQuery = useMemo(
    () => ({
      skip: activeTab !== 'content',
    }),
    [activeTab]
  )

  // Content queries
  const {
    data: annotatedContent,
    isLoading: isAnnotatedLoading,
    isFetching: isAnnotatedFetching,
    refetch: refetchAnnotated,
  } = useFetchAnnotatedContentQuery(annotatedContentArgs, {
    ...annotatedContentQuery,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: unannotatedContent,
    isLoading: isUnannotatedLoading,
    isFetching: isUnannotatedFetching,
    refetch: refetchUnannotated,
  } = useFetchAnnotatedContentQuery(unannotatedContentArgs, {
    ...unannotatedContentQuery,
    refetchOnMountOrArgChange: true,
  })

  // Annotation statistics (consolidated)
  const datasetStatsMap = useDatasetStats(
    selectedProjectId || '',
    project?.datasetid || '',
    datasetSources?.includes('All') ? [] : datasetSources || []
  )
  const aggregateStats = datasetStatsMap.__aggregate__

  // Refetch content when component mounts or becomes active
  useEffect(() => {
    const refetchContent = async () => {
      try {
        if (activeTab === 'annotated') {
          await refetchAnnotated()
        } else if (activeTab === 'content') {
          await refetchUnannotated()
        }
      } catch (error) {
        setErrorMessage(
          error
            ? (error as any).data?.message || 'Error refetching content'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
    refetchContent()
  }, [refetchAnnotated, refetchUnannotated, activeTab])

  // Filtered content for both tabs
  const filteredAnnotatedContent = useMemo(() => {
    const contentToProcess = annotatedContent?.content || []

    if (selectedFilterTags?.length > 0) {
      return contentToProcess?.filter((content: Content) => {
        if (selectedFilterTags.some((tag) => tag.name === 'Unlabeled')) {
          return (
            content.annotation &&
            content.annotation.length > 0 &&
            (!content.annotation[0].tagids || content.annotation[0].tagids.length === 0)
          )
        }
        const contentTagIds = content.annotation?.[0]?.tagids || []
        return contentTagIds?.some((tagId: string) =>
          selectedFilterTags?.map((t) => t.id).includes(tagId)
        )
      })
    }
    return contentToProcess
  }, [annotatedContent?.content, selectedFilterTags])

  const filteredUnannotatedContent = useMemo(() => {
    return unannotatedContent?.content || []
  }, [unannotatedContent?.content])

  // Navigation handlers
  const handleNextImage = () => {
    const currentContent = selectedContent
    if (!currentContent) return

    const currentFilteredContent =
      activeTab === 'annotated' ? filteredAnnotatedContent : filteredUnannotatedContent
    if (!currentFilteredContent || currentFilteredContent.length === 0) return

    const currentIndex = currentFilteredContent.findIndex(
      (content: Content) => content.id === currentContent.id
    )
    if (currentIndex === -1) {
      const firstContent = currentFilteredContent[0]
      if (firstContent) {
        resetStatesAndNavigate(firstContent)
      }
      return
    }

    if (currentIndex >= currentFilteredContent.length - 1) return

    const nextContent = currentFilteredContent[currentIndex + 1]
    if (nextContent) {
      resetStatesAndNavigate(nextContent)
    }
  }

  const handlePrevImage = () => {
    const currentContent = selectedContent
    if (!currentContent) return

    const currentFilteredContent =
      activeTab === 'annotated' ? filteredAnnotatedContent : filteredUnannotatedContent
    if (!currentFilteredContent || currentFilteredContent.length === 0) return

    const currentIndex = currentFilteredContent.findIndex(
      (content: Content) => content.id === currentContent.id
    )
    if (currentIndex === -1) {
      const lastContent = currentFilteredContent[currentFilteredContent.length - 1]
      if (lastContent) {
        resetStatesAndNavigate(lastContent)
      }
      return
    }

    if (currentIndex <= 0) return

    const prevContent = currentFilteredContent[currentIndex - 1]
    if (prevContent) {
      resetStatesAndNavigate(prevContent)
    }
  }

  // Helper function to reset states and navigate to new content
  const resetStatesAndNavigate = (content: Content) => {
    dispatch(clearBBoxes())
    dispatch(clearInitialBoundingBoxes())
    dispatch(clearEditingTagIds())
    dispatch(setSelectedBoundingBox(undefined))
    dispatch(clearUndoStack())
    dispatch(clearRedoStack())

    setSelectedContent(content)
    dispatch(setEditingContent(content))
    dispatch(setSelectedContentId(content.id))
  }

  const handleContentSelect = (content: Content) => {
    setSelectedContent(content)
  }

  const handleBackToGallery = () => {
    setSelectedContent(null)
    // Clear Redux state to prevent reopening the same content
    dispatch(clearEditingContent())
    dispatch(setSelectedContentId(''))
  }

  const handleTabSelect = useCallback(
    (tab: string | null) => {
      if (tab) {
        const typedTab = tab as ActiveTab
        setLocalActiveTab(typedTab)
        dispatch(clearAllBoundingBoxes())
        dispatch(setActiveTab(typedTab))
        if (tab === 'content') {
          dispatch(clearSelectedModelTab())
          dispatch(setFilterTags([]))
        } else if (tab === 'annotated') {
          dispatch(clearSelectedModelTab())
        } else {
          dispatch(setSelectedModelTab(tab))
        }
      }
    },
    [dispatch]
  )

  const handlePageChange = (pageNumber: number) => {
    if (activeTab === 'annotated') {
      const numAnnotated = aggregateStats?.total_annotated_images || 0
      const newPage =
        pageNumber >= 0 && pageNumber < Math.trunc(numAnnotated / 200) + 1
          ? pageNumber
          : Math.trunc(numAnnotated / 200) + 1

      dispatch(setAnnotatedPage(newPage))
    } else {
      const numUnannotated = aggregateStats?.total_unannotated_images || 0
      const newPage =
        pageNumber >= 0 && pageNumber < Math.trunc(numUnannotated / 200) + 1
          ? pageNumber
          : Math.trunc(numUnannotated / 200) + 1
      dispatch(setUnannotatedPage(newPage))
    }
  }

  // Auto-switch to annotated tab if no unannotated content
  useEffect(() => {
    if (
      activeTab === 'content' &&
      !isUnannotatedLoading &&
      !isUnannotatedFetching &&
      unannotatedContent?.content?.length === 0
    ) {
      handleTabSelect('annotated')
    }
  }, [
    activeTab,
    isUnannotatedLoading,
    isUnannotatedFetching,
    unannotatedContent?.content?.length,
    handleTabSelect,
  ])

  // Check for content to open from Redux (e.g., when navigating from Label button)
  // Only sync if the editingContent belongs to the current project
  useEffect(() => {
    if (editingContent && !selectedContent && editingContent.projectid === selectedProjectId) {
      setSelectedContent(editingContent)
    }
  }, [editingContent, selectedContent, selectedProjectId])

  return selectedContent ? (
    <AnnotationsReview
      content={selectedContent}
      onBack={handleBackToGallery}
      onNext={handleNextImage}
      onPrevious={handlePrevImage}
      filteredAnnotatedContent={filteredAnnotatedContent}
      filteredUnannotatedContent={filteredUnannotatedContent}
      activeTab={activeTab}
    />
  ) : (
    <AnnotationsSubView
      project={project}
      itemsPerPage={200}
      onContentSelect={handleContentSelect}
      activeTab={activeTab}
      onTabSelect={handleTabSelect}
      onPageChange={handlePageChange}
      filteredAnnotatedContent={filteredAnnotatedContent}
      filteredUnannotatedContent={filteredUnannotatedContent}
      isAnnotatedLoading={isAnnotatedLoading || isAnnotatedFetching}
      isUnannotatedLoading={isUnannotatedLoading || isUnannotatedFetching}
      annoStats={aggregateStats ? { statistics: aggregateStats } : (undefined as any)}
      errorMessage={errorMessage}
      showError={showError}
      onErrorHide={() => setShowError(false)}
      annotatedPage={annotatedPage}
      unannotatedPage={unannotatedPage}
    />
  )
}

export default DatasetLabelingView
